/**
 * Curriculum Planner — Executive Dashboard (landing page).
 *
 * Real-time executive summary of KISSED University development.
 * The primary operational dashboard for university leadership.
 *
 * Route: /kcis/admin/university/curriculum-planner
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import KcisAdminShell, { KCIS_ADMIN_PALETTE as PAL } from "@/pages/kcis_admin/KcisAdminShell";
import { curriculumApi, PLANNER_STATUS_COLOR, PLANNER_STATUS_LABEL } from "@/lib/curriculumApi";

const CARD = {
  background: PAL.panel, border: `1px solid ${PAL.border}`, borderRadius: 14,
};

function Metric({ label, value, tone, testid, sub }) {
  return (
    <div style={{ ...CARD, padding: 16 }} data-testid={testid}>
      <div className="text-[11px] uppercase tracking-widest" style={{ color: PAL.muted }}>{label}</div>
      <div className="mt-1 text-2xl font-semibold" style={{ color: tone || PAL.text }}>{value}</div>
      {sub && <div className="mt-1 text-xs" style={{ color: PAL.muted }}>{sub}</div>}
    </div>
  );
}

function ProgressBar({ pct, tone }) {
  return (
    <div style={{ height: 6, borderRadius: 999, background: "#211A2B", overflow: "hidden" }}>
      <div style={{
        width: `${Math.min(100, Math.max(0, pct))}%`,
        height: "100%",
        background: tone || PAL.accent,
      }} />
    </div>
  );
}

export default function ExecutiveDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ college: "", school: "", department: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await curriculumApi.executiveDashboard(filters);
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load dashboard");
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const t = data?.totals || {};
  const programs = data?.programs || [];
  const filtersOpts = data?.filters || {};

  return (
    <KcisAdminShell>
      <div className="p-6 space-y-6"
           style={{ background: PAL.bg, color: PAL.text, minHeight: "100vh" }}
           data-testid="executive-dashboard-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-widest" style={{ color: PAL.muted }}>
              KCIS Admin · University · Curriculum Planner
            </div>
            <h1 className="text-3xl font-semibold mt-1">Executive Dashboard</h1>
            <p className="text-sm mt-1 max-w-3xl" style={{ color: PAL.muted }}>
              Real-time operational summary of KISSED University — colleges, programs, curriculum development, AI generation pipeline, faculty workload, and launch readiness.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Link to="/kcis/admin/university/curriculum-planner/programs"
                  className="px-3 py-2 rounded-lg text-sm"
                  style={{ background: PAL.accent, color: "#12091E" }}
                  data-testid="nav-programs-btn">
              Programs & Courses
            </Link>
            <Link to="/kcis/admin/university/curriculum-planner/competencies"
                  className="px-3 py-2 rounded-lg text-sm"
                  style={{ background: PAL.accentSoft, color: PAL.text }}
                  data-testid="nav-competencies-btn">
              Competency Matrix
            </Link>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-end" data-testid="dashboard-filters">
          <FilterSelect label="College" value={filters.college} onChange={(v) => setFilters(f => ({ ...f, college: v }))}
                        options={filtersOpts.colleges || []} testid="filter-college" />
          <FilterSelect label="School" value={filters.school} onChange={(v) => setFilters(f => ({ ...f, school: v }))}
                        options={filtersOpts.schools || []} testid="filter-school" />
          <FilterSelect label="Department" value={filters.department} onChange={(v) => setFilters(f => ({ ...f, department: v }))}
                        options={filtersOpts.departments || []} testid="filter-department" />
          {(filters.college || filters.school || filters.department) && (
            <button onClick={() => setFilters({ college: "", school: "", department: "" })}
                    className="text-xs underline" style={{ color: PAL.muted }}
                    data-testid="filter-clear-btn">Clear filters</button>
          )}
        </div>

        {loading && <div className="text-sm" style={{ color: PAL.muted }}>Loading…</div>}

        {data && (
          <>
            {/* Hero completion card */}
            <div style={{ ...CARD, padding: 24 }} data-testid="hero-completion">
              <div className="flex items-baseline justify-between flex-wrap gap-3">
                <div>
                  <div className="text-[11px] uppercase tracking-widest" style={{ color: PAL.muted }}>
                    Overall University Completion
                  </div>
                  <div className="mt-1 text-5xl font-semibold" style={{ color: PAL.accent }}
                       data-testid="overall-university-pct">
                    {t.overall_university_completion_pct}%
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm" style={{ color: PAL.muted }}>
                    {t.courses_published || 0} of {t.total_courses || 0} courses published
                  </div>
                  <div className="text-xs mt-1" style={{ color: PAL.muted }}>
                    {t.total_published_hours || 0} of {t.total_planned_hours || 0} total student hours
                  </div>
                </div>
              </div>
              <div className="mt-3">
                <ProgressBar pct={t.overall_university_completion_pct || 0} />
              </div>
            </div>

            {/* Structure metrics */}
            <div>
              <div className="text-xs uppercase tracking-widest mb-2" style={{ color: PAL.muted }}>
                Academic Structure
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Metric label="Colleges" value={t.total_colleges || 0}
                         sub={`${t.colleges_complete || 0} complete`} testid="metric-colleges" />
                <Metric label="Schools" value={t.total_schools || 0} testid="metric-schools" />
                <Metric label="Departments" value={t.total_departments || 0} testid="metric-departments" />
                <Metric label="Programs" value={t.total_programs || 0}
                         sub={`${t.programs_active || 0} active · ${t.programs_in_development || 0} in dev`}
                         testid="metric-programs" />
              </div>
            </div>

            {/* Course pipeline */}
            <div>
              <div className="text-xs uppercase tracking-widest mb-2" style={{ color: PAL.muted }}>
                Course Development Pipeline
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Metric label="Planned" value={t.courses_planned || 0}
                         tone={PLANNER_STATUS_COLOR.planned} testid="metric-planned" />
                <Metric label="In Development" value={t.courses_in_development || 0}
                         tone={PLANNER_STATUS_COLOR.in_development} testid="metric-in-development" />
                <Metric label="Ready for Review" value={t.courses_ready_for_review || 0}
                         tone={PLANNER_STATUS_COLOR.ready} testid="metric-ready" />
                <Metric label="Published" value={t.courses_published || 0}
                         tone={PLANNER_STATUS_COLOR.active} testid="metric-published" />
                <Metric label="Retired" value={t.courses_retired || 0}
                         tone={PLANNER_STATUS_COLOR.retired} testid="metric-retired" />
              </div>
            </div>

            {/* Waiting queue */}
            <div>
              <div className="text-xs uppercase tracking-widest mb-2" style={{ color: PAL.muted }}>
                Waiting Queue (KAPE production)
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <Metric label="AI Generation" value={t.waiting_ai_generation || 0} testid="metric-wait-ai" />
                <Metric label="Narration" value={t.waiting_narration || 0} testid="metric-wait-narration" />
                <Metric label="Images" value={t.waiting_images || 0} testid="metric-wait-images" />
                <Metric label="Faculty Review" value={t.waiting_faculty_review || 0} testid="metric-wait-faculty" />
                <Metric label="Quality Review" value={t.waiting_quality_review || 0} testid="metric-wait-quality" />
                <Metric label="Ready to Publish" value={t.ready_to_publish || 0}
                         tone={PAL.accent} testid="metric-ready-to-publish" />
              </div>
            </div>

            {/* Program list */}
            <div>
              <div className="text-xs uppercase tracking-widest mb-2" style={{ color: PAL.muted }}>
                Programs · Drill down
              </div>
              <div style={{ ...CARD, padding: 0, overflow: "hidden" }}>
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ background: "#211A2B", color: PAL.muted }}>
                      <th className="text-left px-4 py-2">Program</th>
                      <th className="text-left px-4 py-2">College · School · Dept</th>
                      <th className="text-right px-4 py-2">Courses</th>
                      <th className="text-right px-4 py-2">Active</th>
                      <th className="text-left px-4 py-2 w-40">Completion</th>
                      <th className="text-left px-4 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {programs.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-4 py-6 text-center"
                            style={{ color: PAL.muted }} data-testid="programs-empty">
                          No programs yet · <Link to="/kcis/admin/university/curriculum-planner/programs"
                                                    className="underline" style={{ color: PAL.accent }}>
                            Create your first program
                          </Link>
                        </td>
                      </tr>
                    )}
                    {programs.map((p) => (
                      <tr key={p.program_id} className="border-t"
                          style={{ borderColor: PAL.border }} data-testid={`program-row-${p.program_id}`}>
                        <td className="px-4 py-3">
                          <Link to={`/kcis/admin/university/curriculum-planner/programs/${p.program_id}`}
                                className="font-medium hover:underline"
                                style={{ color: PAL.text }}
                                data-testid={`program-link-${p.program_id}`}>
                            {p.program_name || "(unnamed)"}
                          </Link>
                        </td>
                        <td className="px-4 py-3" style={{ color: PAL.muted }}>
                          {[p.college, p.school, p.department].filter(Boolean).join(" · ")}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {p.total_courses}{p.required_courses_count ? ` / ${p.required_courses_count}` : ""}
                        </td>
                        <td className="px-4 py-3 text-right">{p.active_courses}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="flex-1"><ProgressBar pct={p.completion_pct} /></div>
                            <span className="text-xs" style={{ color: PAL.muted, width: 32 }}>{p.completion_pct}%</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 rounded-md text-[11px] uppercase tracking-widest"
                                 style={{ background: PAL.accentSoft, color: PAL.text }}>
                            {p.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Faculty workload */}
            {(data.faculty_workload || []).length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-widest mb-2" style={{ color: PAL.muted }}>
                  Faculty Workload (courses assigned)
                </div>
                <div style={{ ...CARD, padding: 12 }}>
                  {data.faculty_workload.map((f) => (
                    <div key={f.instructor_id} className="flex items-center justify-between py-1 text-sm"
                         data-testid={`faculty-workload-${f.instructor_id}`}>
                      <span style={{ color: PAL.muted }}>{f.instructor_id}</span>
                      <span>{f.course_count} course{f.course_count === 1 ? "" : "s"}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </KcisAdminShell>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <label className="text-xs" style={{ color: PAL.muted }}>
      <div className="mb-1">{label}</div>
      <select value={value} onChange={(e) => onChange(e.target.value)}
              data-testid={testid}
              className="px-2 py-1 rounded-md text-sm"
              style={{ background: PAL.panel, border: `1px solid ${PAL.border}`, color: PAL.text, minWidth: 160 }}>
        <option value="">All</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
